import React, { useState, useEffect } from 'react';
import { Task, CreateTaskInput } from '../../services/taskService';
import { TaskForm } from './TaskForm';
import { X } from 'lucide-react';
import { createPortal } from 'react-dom';

interface TaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (task: CreateTaskInput) => Promise<void>;
  task?: Task;
  projectId?: string; // Auto-populate project when opened from project context
  title?: string;
  description?: string;
}

export const TaskModal: React.FC<TaskModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  task,
  projectId,
  title = 'New Task',
  description = 'Create a new task',
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (taskData: CreateTaskInput) => {
    setIsSubmitting(true);
    try {
      // Auto-populate project_id if provided (from project context)
      const taskWithProject = {
        ...taskData,
        ...(projectId && { project_id: projectId }),
      };

      await onSubmit(taskWithProject);
      onClose();
    } catch (error) {
      console.error('Error submitting task:', error);
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  if (!isOpen) {
    return null;
  }

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[300] bg-black/50"
        onClick={handleCancel}
      />
      {/* Modal */}
      <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 pointer-events-none">
        <div className="w-[95vw] max-w-2xl h-[90vh] sm:max-h-[90vh] relative bg-background border shadow-lg rounded-lg p-0 overflow-hidden flex flex-col pointer-events-auto">
          {/* Header */}
          <div className="px-4 sm:px-6 pt-4 sm:pt-6 pb-4 border-b border-border flex-shrink-0">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg sm:text-xl font-semibold text-foreground">
                  {task ? 'Edit Task' : title}
                </h2>
                <p className="text-sm text-muted-foreground mt-1">
                  {task ? 'Update your task details' : description}
                </p>
              </div>
              <button
                onClick={handleCancel}
                disabled={isSubmitting}
                className="rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Scrollable Content */}
          <div className="overflow-y-auto flex-1 px-4 sm:px-6 pb-4 sm:pb-6 min-h-0 overscroll-contain">
            <TaskForm
              task={task || {
                id: '',
                project_id: projectId || '',
                title: '',
                description: '',
                priority: 'medium',
                status: 'todo',
                user_id: '',
                assignee_id: '',
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
              } as Task}
              onSubmit={handleSubmit}
              onCancel={handleCancel}
              inDialog={true}
              isSubmitting={isSubmitting}
            />
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};